import { useEffect, useState } from 'react';

/**
 * Real safe-area numbers off a real device, behind ?safearea=1.
 *
 * iOS reports env(safe-area-inset-*) and the dvh/svh/lvh units differently in a
 * Safari tab, a home-screen app, and with the toolbar shown vs collapsed —
 * and none of it is reproducible in a desktop browser, which reports 0 for
 * every inset. Guessing at these values got the bottom-nav clipping wrong once
 * already, so this prints them instead.
 *
 * The line that matters is navBottom vs innerHeight: if navBottom is larger,
 * the nav is laid out past the bottom of the viewport and is being clipped. If
 * they match, the nav is in the viewport and something is drawn over it.
 */
function readVar(name: string) {
  const el = document.createElement('div');
  el.style.cssText = `position:fixed;visibility:hidden;height:${name}`;
  document.body.appendChild(el);
  const h = getComputedStyle(el).height;
  el.remove();
  return h;
}

export default function SafeAreaProbe() {
  const [, tick] = useState(0);

  useEffect(() => {
    const bump = () => tick((n) => n + 1);
    window.addEventListener('resize', bump);
    window.visualViewport?.addEventListener('resize', bump);
    window.visualViewport?.addEventListener('scroll', bump);
    const t = setInterval(bump, 1000);
    return () => {
      window.removeEventListener('resize', bump);
      window.visualViewport?.removeEventListener('resize', bump);
      window.visualViewport?.removeEventListener('scroll', bump);
      clearInterval(t);
    };
  }, []);

  const nav = document.querySelector('.bottom-nav')?.getBoundingClientRect();
  const frame = document.querySelector('.phone-frame')?.getBoundingClientRect();
  const html = document.documentElement;

  const dsp = (sel: string) => {
    const e = document.querySelector(sel);
    return e ? getComputedStyle(e).display : 'absent';
  };

  const rows: [string, string][] = [
    // Layout identity first: a phone showing the desktop layout is a different
    // bug from a phone whose nav is clipped, and these three tell them apart.
    ['innerWidth', `${window.innerWidth}`],
    ['pf classes', (document.querySelector('.phone-frame')?.className ?? '—').replace('phone-frame', '').trim() || 'NONE'],
    ['desktop chrome', `fab:${dsp('.fab-group')} banner:${dsp('.banner-fab-group')}`],
    ['mq >=641 matches', String(window.matchMedia('(min-width: 641px)').matches)],
    ['mq range ok', String(window.matchMedia('(width >= 641px)').media !== 'not all')],
    ['innerHeight', `${window.innerHeight}`],
    ['visualViewport.h', `${Math.round(window.visualViewport?.height ?? 0)}`],
    ['html clientHeight', `${html.clientHeight}`],
    ['html computed h', getComputedStyle(html).height],
    ['100dvh', readVar('100dvh')],
    ['100svh', readVar('100svh')],
    ['100lvh', readVar('100lvh')],
    ['inset-top', readVar('env(safe-area-inset-top, 0px)')],
    ['inset-bottom', readVar('env(safe-area-inset-bottom, 0px)')],
    ['data-standalone', html.hasAttribute('data-standalone') ? 'YES' : 'no'],
    ['navigator.standalone', String((navigator as { standalone?: boolean }).standalone)],
    ['display-mode', window.matchMedia('(display-mode: standalone)').matches ? 'standalone' : 'browser'],
    ['nav top', nav ? `${Math.round(nav.top)}` : '—'],
    ['nav bottom', nav ? `${Math.round(nav.bottom)}` : '—'],
    ['nav height', nav ? `${Math.round(nav.height)}` : '—'],
    ['nav padBottom', nav ? getComputedStyle(document.querySelector('.bottom-nav')!).paddingBottom : '—'],
    ['frame bottom', frame ? `${Math.round(frame.bottom)}` : '—'],
  ];

  const clipped = nav ? Math.round(nav.bottom - window.innerHeight) : 0;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 2147483647,
      background: 'rgba(0,0,0,0.88)', color: '#0f0', font: '11px/1.35 ui-monospace,monospace',
      padding: '6px 8px', pointerEvents: 'none', whiteSpace: 'pre',
    }}>
      {rows.map(([k, v]) => `${k.padEnd(20)} ${v}`).join('\n')}
      {'\n'}
      <span style={{ color: clipped > 0 ? '#f55' : '#0f0' }}>
        {`nav past viewport   ${clipped > 0 ? `${clipped}px CLIPPED` : 'no'}`}
      </span>
    </div>
  );
}
